import * as React from 'react';
import * as DateFns from 'date-fns';
import Dropzone, { Accept } from 'react-dropzone';
import '../css/file-upload.sass';
import FileUploadProgress from './FileUploadProgress';

export interface DropZoneProps {
  header?: string;
  body?: string;
  maxFileSize: number;
  fileTypes: string[];
  onFileSizeError: Function;
  onFileTypeError: Function;
  onFileAccepted: any;
  uploadProgress: number[];
  serverError?: string;
  fileTypeError?: string;
  fileSizeError?: string;
  successMessage?: string;
  uploadedFiles: any;
  uploadedOn: string[];
  maxFiles?: number;
  onMaxFilesExceededError?: Function;
  displayRemoveButton?: boolean;
  removeFileHeading?: string;
  removeFileMsg?: string;
  removeFileNoBtnLbl?: string;
  removeFileYesBtnLbl?: string;
  systemErrorMessage?: string;
  failedFileIndex?: number;
  onRemoveFile?: Function;
}

export default function DropZone(props: DropZoneProps) {
  const {
    header = 'Drag file(s) to upload or click to browse',
    body = 'Accepted filetypes: JPG, JPEG, PNG, GIF, or BMP, Maximum filesize 10MB',
    fileTypeError = 'Uh-oh, looks like you used an unsupported filetype. Please use JPG, JPEG, PNG, GIF, or BMP.',
    fileSizeError = 'Dang! Your file is too large. Please keep it under 10MB.',
    successMessage = 'Awesome, we got it. Thanks!'
  } = props;

  const [status, setStatus] = React.useState<
    'error' | 'success' | 'info' | 'warning' | 'highlight' | 'default'
  >('default');
  const [errorMessage, setErrorMessage] = React.useState<string[]>([]);
  const [displayProgress, setDisplayProgress] = React.useState<boolean>(false);
  const [file, setFile] = React.useState([]);
  const [validFilesCount, setValidFilesCount] = React.useState<number>(0);

  const fileRejected = (fileRejections: any) => {
    if (
      props.maxFiles &&
      validFilesCount + fileRejections.length > props.maxFiles
    ) {
      if (props.onMaxFilesExceededError) props.onMaxFilesExceededError(true);
    } else {
      setStatus('error');
      let tempErrorMessages: string[] = [];
      fileRejections.forEach((reject: any) => {
        const fileSize = reject.file.size;
        const fileType = reject.file.type;
        if (fileSize > props.maxFileSize) {
          tempErrorMessages = [...tempErrorMessages, fileSizeError];
          props.onFileSizeError(fileSizeError);
        } else if (!props.fileTypes.includes(fileType)) {
          tempErrorMessages = [...tempErrorMessages, fileTypeError];
          props.onFileTypeError(fileTypeError);
        }
      });
      setErrorMessage(tempErrorMessages);
      setFile(fileRejections);
      if (props.onMaxFilesExceededError) props.onMaxFilesExceededError(false);
      setDisplayProgress(true);
    }
  };
  const date = DateFns.format(new Date(), 'MM/dd/yy . h:mm aaa').toLowerCase();

  const fileAccepted = (acceptedFiles: any) => {
    if (
      props.maxFiles &&
      validFilesCount + acceptedFiles.length > props.maxFiles
    ) {
      if (props.onMaxFilesExceededError) props.onMaxFilesExceededError(true);
    } else {
      setStatus('success');
      setFile(acceptedFiles);
      setValidFilesCount(prev => prev + acceptedFiles.length);
      props.onFileAccepted(acceptedFiles);
      if (props.onMaxFilesExceededError) props.onMaxFilesExceededError(false);
      setDisplayProgress(true);
    }
  };

  React.useEffect(() => {
    if (props.uploadedFiles.length > 0) {
      setDisplayProgress(true);
    }
  }, [props.uploadedFiles]);

  React.useEffect(() => {
    if (props.failedFileIndex !== undefined && props.failedFileIndex >= 0) {
      setValidFilesCount(prev => prev - 1);
    }
  }, [props.failedFileIndex]);

  const fileTypes: Accept = {};

  props.fileTypes.forEach((type: string) => {
    fileTypes[`${type}`] = [type.substring(type.indexOf('/'))];
  })

  return (
    <div>
      <Dropzone
        accept={fileTypes}
        maxSize={props.maxFileSize}
        onDropAccepted={acceptedFiles => fileAccepted(acceptedFiles)}
        onDropRejected={fileRejections => {
          fileRejected(fileRejections);
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <section style={{ marginTop: '32px' }}>
            {displayProgress && (
              <FileUploadProgress
                files={file}
                status={status}
                uploadProgress={props.uploadProgress}
                errorMessage={errorMessage}
                successMessage={successMessage}
                rejectedOn={date}
                uploadedOn={props.uploadedOn}
                uploadedFiles={props.uploadedFiles}
                displayRemoveButton={props.displayRemoveButton}
                onRemoveFile={(files: any) => {
                  setValidFilesCount(prev => prev - 1);
                  if (props.maxFiles && props.onMaxFilesExceededError)
                    props.onMaxFilesExceededError(
                      validFilesCount - 1 > props.maxFiles
                    );
                  if (props.onRemoveFile) props.onRemoveFile(files);
                }}
                removeFileHeading={props.removeFileHeading}
                removeFileMsg={props.removeFileMsg}
                removeFileNoBtnLbl={props.removeFileNoBtnLbl}
                removeFileYesBtnLbl={props.removeFileYesBtnLbl}
                systemErrorMessage={props.systemErrorMessage}
              />
            )}
            <div className="dropzone" {...getRootProps()}>
              <input {...getInputProps()} />
              <div />
              <p className="dropzone-title">{header}</p>
              <p className="dropzone-disclaimer">{body}</p>
            </div>
          </section>
        )}
      </Dropzone>
    </div>
  );
}
